class TimondMobilePurchase extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('.shopify-section');
    this.entryButton = this.querySelector('[data-entry]');
    this.previewButton = this.querySelector('[data-preview]');
    this.cartButton = this.querySelector('[data-cart]');
    this.personalized = this.dataset.personalized === 'true';
    this.mode = 'entry';
    this.pastActions = false;
    this.abort = new AbortController();
    const options = { signal: this.abort.signal };
    this.schedule = () => {
      if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
    };
    window.addEventListener('scroll', this.schedule, { ...options, passive: true });
    window.addEventListener('resize', this.schedule, options);
    window.visualViewport?.addEventListener('resize', this.schedule, options);
    document.addEventListener('focusin', this.schedule, options);
    document.addEventListener('focusout', this.schedule, options);
    this.scope.addEventListener('input', this.schedule, options);
    this.scope.addEventListener('change', this.schedule, options);
    this.scope.addEventListener('load', this.schedule, { ...options, capture: true });
    this.scope.addEventListener('error', this.schedule, { ...options, capture: true });
    this.entryButton.addEventListener('click', () => this.activate('entry'), options);
    this.previewButton.addEventListener('click', () => this.activate('preview'), options);
    this.cartButton.addEventListener('click', () => this.activate('cart'), options);
    this.observer = new MutationObserver(this.schedule);
    this.observer.observe(this.scope, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'aria-invalid', 'class', 'src', 'style', 'hidden', 'value', 'checked'] });
    this.overlayObserver = new MutationObserver(this.schedule);
    this.overlayObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open', 'aria-expanded'] });
    this.schedule();
  }

  controls() {
    if (!this.personalized) {
      const cart = this.scope.querySelector('product-form-component [ref="addToCartButton"]');
      return { cart, buttons: cart ? [cart] : [] };
    }
    const form = this.scope.querySelector('.tee-form-wrapper');
    const fields = Array.from(form?.querySelectorAll('.tee-field') || []).filter(field => {
      const rect = field.getBoundingClientRect();
      return !field.closest('.sr-only, [hidden], [aria-hidden="true"]')
        && rect.width > 1 && rect.height > 1 && getComputedStyle(field).visibility !== 'hidden';
    });
    const preview = form?.querySelector('.tee-btn--preview');
    const cart = form?.querySelector('.tee-btn--atc');
    const incomplete = fields.filter(field => !this.fieldComplete(field));
    return {
      entry: fields.find(field => field.classList.contains('tee-field--invalid')) || incomplete[0] || fields[0] || form,
      complete: fields.length > 0 && incomplete.length === 0,
      preview, cart,
      buttons: [preview, cart].filter(Boolean),
    };
  }

  fieldComplete(field) {
    if (field.classList.contains('tee-field--invalid')) return false;
    const problems = field.querySelectorAll('.tee-photo-error, .tee-photo-uploading, [aria-invalid="true"], [aria-busy="true"]');
    if ([...problems].some(node => node.getClientRects().length > 0)) return false;
    if (field.classList.contains('tee-field--photo')) {
      const image = field.querySelector('.tee-photo-uploaded img');
      return !!image && image.complete && image.naturalWidth > 0;
    }
    const inputs = [...field.querySelectorAll('input:not([type="hidden"]):not([type="file"]), textarea, select')]
      .filter(input => !input.disabled && input.getClientRects().length > 0);
    if (!inputs.length) return false;
    const choices = inputs.filter(input => input.type === 'radio' || input.type === 'checkbox');
    const values = inputs.filter(input => !choices.includes(input));
    return (!choices.length || choices.some(input => input.checked))
      && values.every(input => input.value.trim().length > 0 && input.validity?.valid !== false);
  }

  disabled(target) {
    return !target || target.disabled || target.getAttribute('aria-disabled') === 'true'
      || target.getAttribute('aria-busy') === 'true';
  }

  blocked() {
    const editing = document.activeElement?.matches('input, textarea, select, [contenteditable="true"]');
    const overlay = document.querySelector('dialog[open], theme-drawer[open], header-drawer > details[open], .tee-dialog .vm--modal[aria-expanded="true"]');
    return editing || !!overlay;
  }

  update() {
    const controls = this.controls();
    this.dataset.ready = String(innerWidth <= 989 && !!controls.cart);
    const height = window.visualViewport?.height || innerHeight;
    const header = document.querySelector('#header-component');
    const top = Math.max(0, header?.getBoundingClientRect().bottom || 0);
    const rects = controls.buttons.map(button => button.getBoundingClientRect()).filter(rect => rect.width > 0 && rect.height > 0);
    const end = rects.length ? Math.max(...rects.map(rect => rect.bottom)) : Infinity;
    if (end < top - 24) this.pastActions = true;
    else if (end > top + 24) this.pastActions = false;
    const inlineVisible = rects.some(rect => rect.bottom > top - 24 && rect.top < height + 24);
    const contentBottom = height - 88;
    this.mode = !this.personalized || controls.complete ? 'actions' : 'entry';
    this.dataset.complete = String(!this.personalized || controls.complete);
    const entering = this.personalized && this.mode === 'entry';
    this.entryButton.hidden = !entering;
    this.previewButton.hidden = entering || !this.personalized || !controls.preview;
    this.cartButton.hidden = entering;
    for (const [button, disabled] of [
      [this.entryButton, !controls.entry || !controls.cart],
      [this.previewButton, this.disabled(controls.preview)],
      [this.cartButton, this.disabled(controls.cart)],
    ]) {
      if (button.disabled !== !!disabled) button.disabled = !!disabled;
    }
    const heading = this.scope.querySelector('h1');
    const overviewReached = !heading || heading.getBoundingClientRect().top < contentBottom - 24;
    const inRange = this.personalized ? overviewReached : this.pastActions;
    this.hidden = innerWidth > 989 || !controls.cart || !rects.length || this.blocked()
      || inlineVisible || !inRange;
  }

  activate(action) {
    const controls = this.controls();
    if (this.blocked()) return;
    if (this.personalized && !controls.complete) action = 'entry';
    if (action === 'entry' && this.personalized) {
      const target = controls.entry;
      if (!target || !controls.cart) return;
      const header = document.querySelector('#header-component');
      const offset = (header?.getBoundingClientRect().height || 0) + 16;
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
      window.scrollTo({ top: scrollY + target.getBoundingClientRect().top - offset,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    } else {
      const target = action === 'preview' ? controls.preview : controls.cart;
      if (this.cooldown || this.disabled(target)) return;
      this.cooldown = true;
      this.update();
      target.click();
      this.cooldownTimer = setTimeout(() => { this.cooldown = false; this.schedule(); }, 500);
    }
    this.schedule();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    this.overlayObserver?.disconnect();
    clearTimeout(this.cooldownTimer);
    cancelAnimationFrame(this.frame);
  }
}
if (!customElements.get('timond-mobile-purchase')) customElements.define('timond-mobile-purchase', TimondMobilePurchase);
